'use client';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

type Strategy = 'width' | 'pointer';
type Options = { breakpoint?: number; strategy?: Strategy };

const useClientEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function useIsMobile(opts: Options = {}) {
  const { breakpoint = 1024, strategy = 'width' } = opts;

  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const raf = useRef<number | null>(null);

  useClientEffect(() => {
    // only runs on the client
    const mqWidth = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const mqPointer = window.matchMedia('(pointer: coarse)');

    const compute = () => {
      const byWidth = mqWidth.matches;
      const byPointer = mqPointer.matches;
      setIsMobile(strategy === 'pointer' ? byPointer : byWidth);
    };

    const onChange = () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(compute);
    };

    compute();

    mqWidth.addEventListener?.('change', onChange);
    mqPointer.addEventListener?.('change', onChange);
    window.addEventListener('orientationchange', onChange);
    window.addEventListener('resize', onChange);

    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      mqWidth.removeEventListener?.('change', onChange);
      mqPointer.removeEventListener?.('change', onChange);
      window.removeEventListener('orientationchange', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, [breakpoint, strategy]);

  return isMobile;
}
